import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { handoverApi, tripsApi } from '../api';
import { useAuth } from '../contexts/MemberAuthContext';
import { TRIP_STATUS, TRIP_STATUS_BADGE } from '../utils/trips';
import StatusBadge from '../components/StatusBadge';

export default function HandoverScan() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const navigate = useNavigate();
  const { member } = useAuth();

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selectedTrip, setSelectedTrip] = useState('');
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmConvoy, setConfirmConvoy] = useState(false);
  const [success, setSuccess] = useState(null);

  const isOps = member?.role === 'superadmin' || member?.role === 'ops';
  const tokenPresent = !!token;

  useEffect(() => {
    handoverApi.scan(slug)
      .then((r) => setData(r.data))
      .catch(() => setError('Checkpoint nenalezen nebo byl smazán.'));
  }, [slug]);

  // Po výběru cesty načti její vozidla a předvyber to, kde je přihlášený řidičem.
  useEffect(() => {
    if (!selectedTrip) { setUnits([]); setSelectedUnit(''); return; }
    tripsApi.units(selectedTrip).then((r) => {
      const pending = r.data.filter((u) => !u.arrived_at);
      setUnits(r.data);
      const mine = pending.find((u) => u.driver_id === member?.id);
      setSelectedUnit(mine ? mine.id : (pending[0]?.id || ''));
    }).catch(() => { setUnits([]); setSelectedUnit(''); });
  }, [selectedTrip, member]);

  const doConfirm = async (scope) => {
    setSaving(true); setError('');
    try {
      const r = await handoverApi.confirm(slug, {
        trip_id: selectedTrip,
        trip_vehicle_id: scope === 'vehicle' ? selectedUnit : null,
        scope, token, note,
      });
      setSuccess(r.data);
      setConfirmConvoy(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Chyba při potvrzení příjezdu.');
    } finally { setSaving(false); }
  };

  if (error && !data) {
    return (
      <div className="container text-center" style={{ paddingTop: 60 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ color: 'var(--color-danger)' }}>Checkpoint nenalezen</h2>
        <p className="text-muted mt-8">{error}</p>
        <button className="btn btn-outline mt-16" onClick={() => navigate('/')}>Zpět na hlavní stránku</button>
      </div>
    );
  }
  if (!data) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><div className="spinner" /></div>;
  }

  if (success) {
    const method = success.method === 'qr' ? 'QR skenem' : 'manuálně (OpS)';
    return (
      <div className="container text-center" style={{ paddingTop: 60 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2>Příjezd potvrzen</h2>
        <p className="mt-8">{success.scope === 'convoy'
          ? `Předán celý konvoj (${success.units_confirmed} vozidel).`
          : 'Předáno 1 vozidlo.'}</p>
        <p className="text-muted mt-8">Potvrzeno {method}. Checkpoint: <strong>{data.checkpoint.name}</strong></p>
        <div className="flex gap-8 mt-24" style={{ justifyContent: 'center' }}>
          <button className="btn btn-outline" onClick={() => { setSuccess(null); setSelectedTrip(''); setSelectedUnit(''); setNote(''); }}>
            Potvrdit další
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/')}>Hotovo</button>
        </div>
      </div>
    );
  }

  const { checkpoint, trips } = data;
  const pendingUnits = units.filter((u) => !u.arrived_at);
  const canAct = tokenPresent || isOps;

  return (
    <div className="container" style={{ paddingTop: 32, maxWidth: 560 }}>
      <div className="card mb-16 text-center">
        <div style={{ fontSize: 40, marginBottom: 8 }}>📦</div>
        <h2>{checkpoint.name}</h2>
        <p className="text-muted">MU: <strong>{checkpoint.event_name}</strong></p>
        {checkpoint.event_location && <p className="text-muted" style={{ fontSize: '0.85rem' }}>📍 {checkpoint.event_location}</p>}
        {!tokenPresent && isOps && <div className="badge badge-active mt-8" style={{ display: 'inline-block' }}>Manuální OpS override – token není vyžadován</div>}
        {!tokenPresent && !isOps && <div className="badge badge-inactive mt-8" style={{ display: 'inline-block' }}>⚠️ Chybí token – naskenujte QR kód přímo</div>}
      </div>

      <div className="card mb-16">
        <h3 className="mb-16">1. Vyberte příjíždějící cestu</h3>
        {trips.length === 0 ? (
          <p className="text-muted text-center">Žádná cesta ve stavu „Na cestě" pro tuto MU.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {trips.map((t) => (
              <label key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                border: `2px solid ${selectedTrip === t.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: 8, cursor: 'pointer',
              }}>
                <input type="radio" name="trip" value={t.id} checked={selectedTrip === t.id}
                  onChange={() => setSelectedTrip(t.id)} style={{ accentColor: 'var(--color-primary)' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{t.vehicle_name} {t.vehicle_plate ? `· ${t.vehicle_plate}` : ''}{t.unit_count > 1 ? ` · konvoj ${t.unit_count}×` : ''}</div>
                  <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                    {t.chapter_name}{t.cargo_count > 0 ? ` · ${t.cargo_count} pol. nákladu` : ''}
                  </div>
                </div>
                <StatusBadge status={t.status} label={TRIP_STATUS[t.status]} badgeClass={TRIP_STATUS_BADGE[t.status]} />
              </label>
            ))}
          </div>
        )}
      </div>

      {selectedTrip && (
        <div className="card mb-16">
          <h3 className="mb-16">2. Vyberte své vozidlo</h3>
          {pendingUnits.length === 0 ? (
            <p className="text-muted text-center">Všechna vozidla této cesty už byla předána.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pendingUnits.map((u) => (
                <label key={u.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  border: `2px solid ${selectedUnit === u.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  borderRadius: 8, cursor: 'pointer',
                }}>
                  <input type="radio" name="unit" value={u.id} checked={selectedUnit === u.id}
                    onChange={() => setSelectedUnit(u.id)} style={{ accentColor: 'var(--color-primary)' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>🚚 {u.vehicle_name}{u.vehicle_plate ? ` · ${u.vehicle_plate}` : ''}{u.trailer_name ? ` + 🛒 ${u.trailer_name}` : ''}</div>
                    <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                      {u.driver_first_name ? `řidič: ${u.driver_first_name} ${u.driver_last_name}` : 'bez řidiče'}
                      {u.cargo_count > 0 ? ` · ${u.cargo_count} pol.` : ''}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedTrip && pendingUnits.length > 0 && (
        <>
          <div className="card mb-16">
            <label className="form-label">Poznámka (volitelná)</label>
            <input className="form-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Poznámka k předání…" maxLength={500} />
          </div>

          {error && <div className="alert alert-error mb-16">{error}</div>}

          <div className="flex gap-8" style={{ flexDirection: 'column' }}>
            <button className="btn btn-primary btn-block" disabled={!selectedUnit || saving || !canAct} onClick={() => doConfirm('vehicle')}>
              {saving ? 'Ukládám…' : '✓ Předat toto vozidlo'}
            </button>
            {pendingUnits.length > 1 && (
              <button className="btn btn-outline btn-block" disabled={saving || !canAct} onClick={() => setConfirmConvoy(true)}>
                🚚🚚 Předat celý konvoj ({pendingUnits.length} vozidel)
              </button>
            )}
          </div>

          {!canAct && <p className="text-muted text-center mt-8" style={{ fontSize: '0.82rem' }}>Naskenujte QR kód s tokenem, nebo se přihlaste jako OpS.</p>}
        </>
      )}

      <div className="text-center mt-16">
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/')}>← Zpět</button>
      </div>

      {/* Potvrzovací dialog pro celý konvoj */}
      {confirmConvoy && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
          <div className="card" style={{ maxWidth: 400, textAlign: 'center' }}>
            <div style={{ fontSize: 40 }}>⚠️</div>
            <h3 className="mt-8">Předat celý konvoj?</h3>
            <p className="text-muted mt-8">Chystáte se předat <strong>všech {pendingUnits.length} dosud nepředaných vozidel</strong> této cesty. Jste si jistý?</p>
            <div className="flex gap-8 mt-16">
              <button className="btn btn-outline flex-1" disabled={saving} onClick={() => setConfirmConvoy(false)}>Zrušit</button>
              <button className="btn btn-primary flex-1" disabled={saving} onClick={() => doConfirm('convoy')}>{saving ? 'Ukládám…' : 'Ano, předat konvoj'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
